import React, {useState, useEffect} from 'react';
import { useParams, useLocation} from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';

import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';

import { fetchStoryboardLeadReportSummary } from "actions/leads";

import { LeadSummary } from './components/leadSummary';
import { LeadAnalytics } from '../reports/components/leadAnalytics';
import { DatePickerView } from '../reports/components/datePicker';

import { generateReportUrl, parseQueryParams } from 'services/report.service';

import styles from './leadReports.module.scss';
import { getPageViewedEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_LEAD_REPORTS } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';

const LeadsView =()=> {
  const dispatch = useDispatch();

  const [reportDate, setReportDate] = useState(null);
  const { search } = useLocation();
  let { storyboardId } = useParams();
  const summary = useSelector(state => _.get(state, 'lead.summary.items', {}));

  const storyboardName = useSelector(state => _.get(state, 'storyboards.storyboard.item.name', null));
  const storyboardType = useSelector(state => _.get(state, 'storyboards.storyboard.item.type', null));

  const handleDateChange = (value) => {
    setReportDate(value);
  }

  useEffect(() => {
    const parsedDate = parseQueryParams(search);
    setReportDate(parsedDate);

    dispatch(fetchStoryboardLeadReportSummary(storyboardId));

    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_LEAD_REPORTS, storyboardId, storyboardName, storyboardType)
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
  }, []);

  return (
    <div className={styles.leadContainer} >
      <LeadSummary
        date={reportDate}
      />
      <div className={styles.statisticsView}>
          <LeadStatistics heading="Leads" subheading="Captured" stat={_.get(summary, "leads", 0) || 0} path={'/images/reports/leads.svg'}/>
          <LeadStatistics heading="Accounts" subheading="Influenced" stat={_.get(summary, "account", 0) || 0} path={'/images/leads/accounts.svg'}/>
          <LeadStatistics heading="Conversion" subheading="Rate" stat={`${_.get(summary, "conversion", 0) || 0}%`} path={'/images/leads/conversion.svg'} type="conversionRate"/>
      </div>
      <div className={styles.filters}>
        <DatePickerView
          leadReport={true}
          value={reportDate}
          onChange={handleDateChange}
        />
      </div>  
      <LeadAnalytics
        showProfile={true}
        date={reportDate}
      />
    </div>
  )
}

const LeadStatistics =({heading,subheading,stat,path,iconClassname, type=""})=> {
  const views = useSelector(state => _.get(state, 'storyboards.storyboard.reports.leadTimeSeries.items'));
  return (
    <>
      {type != "conversionRate" &&
        <div className={styles.wrapper}>
          <div>
            <h5 className={styles.heading}>{heading}</h5>
            <p className={styles.sub_heading}>{subheading}</p>
            <p className={styles.stat}>{stat}</p>
          </div>
          <div>
            <img src={path} className={iconClassname}/>
          </div>
        </div>
      }

      {type === "conversionRate" &&
          <div className={styles.wrapper}>
            <div>
              <h5 className={styles.heading}>{heading}</h5>
              <p className={styles.sub_heading}>{subheading}</p>
              <p className={styles.stat}>{stat}</p>
            </div>
            <div>
              <div className={cx(styles.graphFullView, { [styles.emptyGraph]: (views && views.length == 0) })}>
                <LeadGraphView data={views} height={90}/>
                <div className={styles.chartLable}>
                  <span> </span>Leads</div>
              </div>
            </div>
          </div>
      }
    </>
  )
}

const LeadGraphView =({data, height})=> {
  if (!data || data.length < 1) {
    return null;
  }

  var chartData = data.map(item => {
    return {
      key: moment(item.key, 'DD-MM-YYYY').format('DD MMM \nYYYY'),
      value: item.value
    }
  })

  const CustomTick = ({ x, y, payload }) => {
    const [line1, line2] = payload.value.split('\n');
    const offset = 8;
  
    return (
      <foreignObject x={x - 25} y={y + offset} width="50" height="50">
        <div style={{ textAlign: 'center', fontSize: '12px', fontFamily: 'Arial' }}>
          <div>{line1}</div>
          <div>{line2}</div>
        </div>
      </foreignObject>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{
          top: 4,
          right: 0,
          left: -40,
          bottom: 0
        }}
      >
        <CartesianGrid stroke='#EEEEEE' strokeDasharray="0 0"/>
        <XAxis 
          axisLine={{ stroke: '#D5D5D5' }} 
          tickLine={{ stroke: '#FFFFFF' }} 
          dy={0} 
          tick={<CustomTick/>} 
          tickCount={100} 
          dataKey="key"
        />
        <YAxis 
          axisLine={{ stroke: '#D5D5D5' }} 
          tickLine={{ stroke: '#FFFFFF' }} 
          dx={0} 
          tick={{ fontSize: 12 }}
          domain={(chartData || []).every(dataPoint => dataPoint.value < 4) ? [0, 4] : undefined}
        />
        <Line 
          type="monotone" 
          dataKey="value" 
          stroke="#FFC600" 
          strokeWidth={2} 
          dot={false}
          activeDot={true} 
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

export default LeadsView